import { takeEvery, take, put, select } from 'redux-saga/effects';

import { getAccessTokenRemainingMs, REFRESH_THRESHOLD_MS } from '@/utils/auth-token';

import type { RootState } from '../';
import * as actionTypes from '../constants';
import { authenticationCheck, authenticationRefresh } from '../slices/authentication';

function* checkAuthentication(): Generator<unknown, void, any> {
  // Only ROTATE the token when we actually need to. The backend rotates refresh
  // tokens — issuing a new one deletes the old refresh token AND invalidates the
  // old access token — so refreshing on every boot needlessly churns tokens (and
  // re-opens the stale-token race below). Rotate only when the current access
  // token is within a minute of expiry, or when we have no token / can't read its
  // expiry, where refreshing is the safe fallback (matches the prior behaviour).
  const { access_token, expires } = (yield select(
    (state: RootState) => state.authentication,
  )) as RootState['authentication'];
  const remainingMs = getAccessTokenRemainingMs(expires);
  const shouldRefresh = access_token == null || remainingMs == null || remainingMs < REFRESH_THRESHOLD_MS;

  if (shouldRefresh) {
    // Refresh FIRST, and WAIT for it to settle before doing anything else.
    //
    // Previously this saga fired refresh + check concurrently; the check then
    // POSTed the OLD access token (still in localStorage) to /auth/check, which
    // the rotation had just killed — so check failed and its `rejected` handler
    // wiped the stored session, bouncing every hard refresh to /register.
    // Sequencing fixes it.
    yield put(authenticationRefresh());
    const result = yield take([authenticationRefresh.fulfilled.type, authenticationRefresh.rejected.type]);

    // A successful refresh already returned fresh tokens + user data and marked
    // the session authenticated, so the validating check is redundant — and
    // running it would only re-introduce the stale-token problem.
    const authenticated = authenticationRefresh.fulfilled.match(result) && Boolean(result.payload?.success);
    if (authenticated) {
      return;
    }
  }

  // Either the access token is still good (no refresh needed) or refresh did not
  // sign us in (e.g. there was no refresh token to rotate). Validate whatever
  // access token we still hold against /auth/check — that also (re)loads the user
  // and marks the session authenticated; with no token it resolves signed-out.
  yield put(authenticationCheck());
}

export function* watchAuthenticationSaga() {
  yield takeEvery(actionTypes.application.INITIALIZE, checkAuthentication);
}
